import type { ErrorEvent, Event, EventHint } from "@sentry/nextjs";
import { scrubSensitiveData } from "./sentry-pii-scrub";

/** Mirrors @sentry/core's TransactionEvent, which @sentry/nextjs doesn't re-export. */
export type TransactionEvent = Event & { type: "transaction" };

/**
 * Both fingerprinters below exist because this app reports most handled
 * GraphQL errors through a handful of shared wrapper functions (see
 * packages/common/src/lib/query-client-sentry) — the TanStack Query cache
 * subscribers and `reportGraphQlQueryErrorToSentry`. Those wrappers collapse
 * to identical minified stack frames, so Sentry's default stack-trace
 * grouping merges completely unrelated errors into one issue. Explicit
 * fingerprints route around that: `gqlClientErrorFingerprint` handles the
 * well-known graphql-request shape with precision, and `genericErrorFingerprint`
 * is a narrower catch-all scoped to that same wrapper (identified via the
 * `errorSource` tag it always sets) so it doesn't touch errors captured
 * directly at their own call site, which already group correctly by stack.
 */

/** Tag value the shared TanStack Query wrapper always sets — see query-client-sentry.ts / utils.ts. */
const TANSTACK_QUERY_ERROR_SOURCES = new Set([
  "tanstack-query",
  "tanstack-query-mutation",
]);

/**
 * True when this event was captured by the shared TanStack Query error
 * wrapper — the one whose collapsed stack frames motivate this fallback.
 * Errors captured directly at their own call site are left alone so they
 * keep Sentry's default (more precise) stack-based grouping.
 */
function isTanStackQueryWrapperError(event: ErrorEvent): boolean {
  const errorSource = event.tags?.["errorSource"];
  return (
    typeof errorSource === "string" &&
    TANSTACK_QUERY_ERROR_SOURCES.has(errorSource)
  );
}

/**
 * Extracts a stable Sentry fingerprint from a graphql-request ClientError.
 *
 * The fingerprint is: ['graphql-client-error', operation, code, messagePrefix]
 *   - operation    : errors[0].path[0] — the GraphQL operation name (e.g. 'signUp')
 *   - code         : errors[0].extensions.code — e.g. 'BAD_REQUEST', 'FORBIDDEN'
 *   - messagePrefix: the part of errors[0].message before the first ':',
 *                    with dynamic IDs (UUIDs, bracketed/parenthesized
 *                    numeric IDs) stripped, capped at 100 chars
 *
 * Returns null for anything that isn't a ClientError — callers should fall
 * back to `genericErrorFingerprint` in that case.
 *
 * The entire function is wrapped in try/catch — it must never throw.
 */
export function gqlClientErrorFingerprint(
  _event: ErrorEvent,
  hint: EventHint,
): string[] | null {
  try {
    const error = hint.originalException;

    // Only handle Error instances
    if (!(error instanceof Error)) return null;

    // Duck-type for graphql-request's ClientError shape specifically — it's
    // the only Error type with both response.errors[] AND request.query,
    // which keeps this from matching unrelated errors (e.g. axios) that
    // merely happen to carry a `.response` property.
    const errorRecord = error as unknown as Record<string, unknown>;

    const request = errorRecord["request"];
    if (request === null || typeof request !== "object") return null;
    if (typeof (request as Record<string, unknown>)["query"] !== "string") {
      return null;
    }

    const response = errorRecord["response"];
    if (response === null || typeof response !== "object") return null;

    const errors = (response as Record<string, unknown>)["errors"];
    if (!Array.isArray(errors) || errors.length === 0) return null;

    const firstError = errors[0] as Record<string, unknown> | null | undefined;
    if (firstError === null || typeof firstError !== "object") return null;

    // operation: first segment of the path array (e.g. 'signUp', 'updatePlanSubscription')
    const path = firstError["path"];
    const operation =
      Array.isArray(path) && typeof path[0] === "string" && path[0].length > 0
        ? path[0]
        : "unknown";

    // code: GraphQL extensions.code (e.g. 'BAD_REQUEST', 'FORBIDDEN', 'INTERNAL_SERVER_ERROR')
    const extensions = firstError["extensions"];
    const code =
      extensions !== null &&
      typeof extensions === "object" &&
      typeof (extensions as Record<string, unknown>)["code"] === "string" &&
      ((extensions as Record<string, unknown>)["code"] as string).length > 0
        ? ((extensions as Record<string, unknown>)["code"] as string)
        : "UNKNOWN";

    // messagePrefix: text before the first ':', with dynamic IDs stripped
    // (mirrors genericErrorFingerprint — GraphQL messages just as often embed
    // an entity ID without a ':' separator, e.g. "Job 1234 not found"),
    // capped at 100 chars
    const rawMessage =
      typeof firstError["message"] === "string" ? firstError["message"] : "";
    const messagePrefix = stripDynamicTokens(
      (rawMessage.split(":")[0] ?? "").trim(),
    ).slice(0, 100);

    return ["graphql-client-error", operation, code, messagePrefix];
  } catch {
    // Never let fingerprinting break error reporting
    return null;
  }
}

/**
 * Strips common dynamic tokens (UUIDs, bracketed/parenthesized numeric IDs,
 * bare numeric IDs) from a message segment so fingerprints don't fragment
 * per-entity (e.g. "for company [10349]" / "for company 10349" and "for
 * company [4614]" / "for company 4614" should each group together).
 *
 * Bare numbers require 3+ digits (with a word boundary) so short, meaningful
 * numbers — HTTP status codes, small counts ("for 2 items") — aren't stripped
 * and accidentally merged with unrelated errors.
 */
function stripDynamicTokens(text: string): string {
  return text
    .replace(
      /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g,
      "<uuid>",
    )
    .replace(/[[(]\d+[\])]/g, "<id>")
    .replace(/\b\d{3,}\b/g, "<id>");
}

/**
 * Picks out whichever call-site-specific tag identifies *what* was being
 * queried/mutated when the shared wrapper captured this error — the one
 * differentiator that survives even when the message itself doesn't (see
 * `tagContext` below). Checked in this order because a single event only
 * ever carries one of these, per capture site in query-client-sentry.ts:
 *   - graphqlPath      : GraphQL query/mutation error paths
 *   - queryKey         : the REST-400 query-cache path (JSON-stringified query key)
 *   - graphqlOperation : the mutation-cache path when the failure is HTTP-level
 *                        (no GraphQL `errors[]`) — the operation name parsed
 *                        from `request.query`. Preferred over mutationKey,
 *                        which is usually just "[]" and would otherwise merge
 *                        every un-keyed mutation's HTTP 400 into one issue.
 *   - mutationKey      : the mutation-cache path (JSON-stringified mutation key)
 * Dynamic IDs embedded in a stringified key (e.g. a company ID inside
 * queryKey) are stripped so this doesn't just move the fragmentation problem
 * from the message into the tag.
 */
function tanStackQueryTagContext(event: ErrorEvent): string | null {
  const tags = event.tags ?? {};
  const raw =
    tags["graphqlPath"] ??
    tags["queryKey"] ??
    tags["graphqlOperation"] ??
    tags["mutationKey"] ??
    null;
  if (typeof raw !== "string" || raw.length === 0) return null;
  return stripDynamicTokens(raw).slice(0, 100);
}

/**
 * Fallback fingerprint for a handled `Error` reported through the shared
 * TanStack Query wrapper that isn't a graphql-request ClientError (Prisma
 * errors, permission errors, plain thrown Errors surfaced by that wrapper,
 * etc.). Scoped to that wrapper (via the `errorSource` tag) because that's
 * the only place collapsed stack frames actually merge unrelated errors —
 * an error captured at its own call site keeps its own distinct stack, and
 * fingerprinting it by message alone would risk merging otherwise-unrelated
 * bugs that happen to share a common message shape (e.g. two different
 * `TypeError: Cannot read properties of undefined` crashes).
 *
 * The fingerprint is: ['generic-error', name, messagePrefix, tagContext?, statusCode?]
 *   - name         : error.name (e.g. 'Error', 'TypeError')
 *   - messagePrefix: error.message before the first ':', with dynamic IDs
 *                    stripped, capped at 100 chars
 *   - tagContext    : graphqlPath/queryKey/graphqlOperation/mutationKey tag, when present (see
 *                    `tanStackQueryTagContext`) — this is what still tells
 *                    two errors apart when the wrapper's own message
 *                    template collapses messagePrefix to the same literal
 *                    for every error it reports (e.g. query-client-sentry.ts's
 *                    REST-400 branch always produces the message
 *                    "TanStack Query Error: ${error}", so messagePrefix is
 *                    always just "TanStack Query Error")
 *   - statusCode    : the statusCode tag, when present
 *
 * Returns null when there's no usable message, or when the event wasn't
 * reported through the shared wrapper, so Sentry's default grouping is
 * untouched for errors we truly have no signal on.
 *
 * The entire function is wrapped in try/catch — it must never throw.
 */
export function genericErrorFingerprint(
  event: ErrorEvent,
  hint: EventHint,
): string[] | null {
  try {
    if (!isTanStackQueryWrapperError(event)) return null;

    const error = hint.originalException;
    if (!(error instanceof Error)) return null;

    const rawMessage = typeof error.message === "string" ? error.message : "";
    if (rawMessage.length === 0) return null;

    const name =
      typeof error.name === "string" && error.name.length > 0
        ? error.name
        : "Error";

    const prefix = (rawMessage.split(":")[0] ?? "").trim();
    const messagePrefix = stripDynamicTokens(prefix).slice(0, 100);
    if (messagePrefix.length === 0) return null;

    const fingerprint = ["generic-error", name, messagePrefix];

    const tagContext = tanStackQueryTagContext(event);
    if (tagContext !== null) fingerprint.push(tagContext);

    const statusCode = event.tags?.["statusCode"];
    if (typeof statusCode === "string" && statusCode.length > 0) {
      fingerprint.push(statusCode);
    }

    return fingerprint;
  } catch {
    return null;
  }
}

/**
 * Sentry beforeSend hook. Apply to all Sentry.init() calls (client, server, edge).
 *
 * Fingerprints the event for grouping, then scrubs sensitive fields
 * (password, token, secret, etc — see sentry-pii-scrub.ts) from the entire
 * event before it leaves the process. Scrubbing always runs, regardless of
 * how the event was captured. If scrubbing itself fails internally, the
 * event is dropped (returns null) rather than sent unredacted.
 *
 * Usage:
 *   Sentry.init({ beforeSend: sentryBeforeSend, ... })
 */
export function sentryBeforeSend(
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null {
  const fingerprint =
    gqlClientErrorFingerprint(event, hint) ??
    genericErrorFingerprint(event, hint);
  if (fingerprint !== null) {
    event.fingerprint = fingerprint;
  }
  return scrubSensitiveData(event);
}

/**
 * Sentry beforeSendTransaction hook. Apply alongside `sentryBeforeSend` in
 * all Sentry.init() calls — `beforeSend` only sees error events, so without
 * this, transaction/span data (URLs, http/db span attributes) goes out
 * unscrubbed. Same fail-closed contract: a scrubbing failure drops the
 * transaction rather than sending it unredacted.
 *
 * Usage:
 *   Sentry.init({ beforeSendTransaction: sentryBeforeSendTransaction, ... })
 */
export function sentryBeforeSendTransaction(
  event: TransactionEvent,
): TransactionEvent | null {
  return scrubSensitiveData(event);
}
